import { useEffect, useState } from "react";
import {
  hardReload,
  isChunkLoadError,
  CHUNK_RELOAD_KEY,
  VERSION_RELOAD_KEY,
} from "../lib/hardReload";

interface VersionInfo {
  buildTime: string;
  version: string;
  commit: string;
}

// Version string baked into THIS bundle at build time (injected via Vite
// `define` from the same version.json that ships with the build). This is the
// baseline we compare the server's live version.json against.
//
// Comparing the running bundle's build version against the server — rather than
// the first runtime fetch against later fetches — is what makes staleness
// detectable at all. A stale client (bundle served from HTTP/edge cache while
// version.json is no-store and always fresh) that loads AFTER a deploy would
// otherwise seed its baseline from the already-new server version and never see
// a mismatch, running old code for the whole session.
declare const __APP_VERSION__: string | undefined;
const BUILD_VERSION: string | null =
  typeof __APP_VERSION__ === "string" && __APP_VERSION__ ? __APP_VERSION__ : null;

let currentVersion: VersionInfo | null = null;
let checkInterval: ReturnType<typeof setInterval> | null = null;
let preventReloadFlag = false;

/**
 * Set whether automatic reloads should be prevented.
 * Use this when displaying critical information (like client secrets) that would be lost on reload.
 */
export function setPreventReload(prevent: boolean) {
  preventReloadFlag = prevent;
}

/**
 * Hook to check for new deployments and prompt user to reload.
 * Prevents 404 errors when old clients try to load new asset chunks.
 */
export function useVersionCheck(options?: {
  checkIntervalMs?: number;
  onNewVersion?: () => void;
}) {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const checkIntervalMs = options?.checkIntervalMs ?? 5 * 60 * 1000; // Default: 5 minutes

  useEffect(() => {
    // __APP_VERSION__ is baked in once, at Vite dev-server startup, from
    // public/version.json on disk (see vite.config.ts). If that file changes
    // on disk afterward — a stray build, a git checkout, a manual edit — the
    // running dev bundle's baseline never re-syncs, and a browser reload can't
    // fix it either (that doesn't restart the Vite process). Only a dev-server
    // restart re-reads the file. This check is for detecting real deploys, not
    // local file drift, so skip it entirely in dev.
    if (import.meta.env.DEV) return;

    const fetchVersion = async () => {
      try {
        // Fetch the version.json that ships WITH this bundle, not the host
        // root. In production the app is served under a sub-app base path
        // (e.g. "/outlook/" or "/remote/"), so an absolute "/version.json"
        // resolves to a different (or missing) deploy's file and would never
        // match this bundle's baked-in version — a permanent false "new
        // version". import.meta.env.BASE_URL is Vite's configured `base`
        // (always trailing-slashed; "/" in dev).
        const versionUrl = `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`;
        const response = await fetch(versionUrl, {
          cache: "no-store",
        });
        if (!response.ok) return;
        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) return;
        const data: VersionInfo = await response.json();

        // Baseline is the version compiled into the running bundle, not the
        // first fetch. Fall back to the first fetch only when the build
        // constant is unavailable (e.g. dev server without the define).
        const baseline = BUILD_VERSION ?? currentVersion?.version ?? null;
        if (baseline === null) {
          currentVersion = data;
          console.log("Initial app version (no build constant):", data.version);
        } else if (baseline !== data.version) {
          console.log(
            "New version detected:",
            data.version,
            "Running:",
            baseline
          );
          setIsNewVersionAvailable(true);
          setLatestVersion(data.version);
          options?.onNewVersion?.();
        }
      } catch (error) {
        // The version check is a best-effort background poll that retries every
        // `checkIntervalMs`. A failed `fetch` (TypeError: "Failed to fetch") is a
        // transient network blip — offline moment, connection reset, DNS — that
        // self-heals on the next tick, so it must NOT be reported as an error.
        // Sentry's console instrumentation captures `console.error`, so use
        // `console.debug` here to keep this expected noise out of Sentry
        // (EXTENSION-9C). Only genuinely unexpected failures should escalate.
        const isNetworkError =
          error instanceof TypeError &&
          /fetch/i.test(error.message ?? "");
        if (isNetworkError) {
          console.debug("Version check skipped (network unavailable):", error);
        } else {
          console.error("Failed to check version:", error);
        }
      }
    };

    void fetchVersion();

    checkInterval = setInterval(() => {
      void fetchVersion();
    }, checkIntervalMs);

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [checkIntervalMs, options]);

  // Auto-reload on safe user navigation when new version is available.
  // One-shot per target version: hardReload() navigates to a brand new
  // page/JS context, so an in-memory flag alone only stops repeat reloads
  // within the current page load, not across the reload itself. If the
  // post-reload page still sees the same target version as stale — a rolling
  // deploy round-robining between old/new pods, or an edge cache still
  // serving the pre-reload bundle — this effect re-runs from scratch and
  // would reload again on the very next click, looping. sessionStorage keyed
  // by the target version stops that: this exact version is auto-reloaded
  // for at most once per session, regardless of how many page loads it takes.
  useEffect(() => {
    if (!isNewVersionAvailable) return;

    let hasTriggeredReload = false;

    const alreadyReloadedForVersion = (): boolean => {
      if (!latestVersion) return false;
      try {
        return sessionStorage.getItem(VERSION_RELOAD_KEY) === latestVersion;
      } catch {
        return false;
      }
    };

    const handleUserInteraction = (event: MouseEvent) => {
      if (hasTriggeredReload || preventReloadFlag || alreadyReloadedForVersion())
        return;

      const target = event.target as HTMLElement;
      const clickedButton = target.closest("button");
      const clickedLink = target.closest("a");

      let shouldReload = false;
      if (clickedLink) {
        const href = clickedLink.getAttribute("href");
        shouldReload = !!href && href !== "#" && !href.startsWith("javascript:");
        if (shouldReload) {
          console.log("Navigation link clicked with new version - reloading");
        }
      } else if (clickedButton) {
        const buttonType = clickedButton.getAttribute("type");
        const isSubmitButton = buttonType === "submit";
        const isInForm = clickedButton.closest("form") !== null;
        shouldReload = !isSubmitButton && !isInForm;
        if (shouldReload) {
          console.log("Safe button clicked with new version - reloading");
        }
      }

      if (shouldReload) {
        hasTriggeredReload = true;
        document.removeEventListener("click", handleUserInteraction);
        if (latestVersion) {
          try {
            sessionStorage.setItem(VERSION_RELOAD_KEY, latestVersion);
          } catch {
            // in-memory hasTriggeredReload still stops a loop within this page load
          }
        }
        hardReload();
      }
    };

    document.addEventListener("click", handleUserInteraction);
    return () => {
      document.removeEventListener("click", handleUserInteraction);
    };
  }, [isNewVersionAvailable, latestVersion]);

  const reloadApp = () => {
    hardReload();
  };

  return {
    isNewVersionAvailable,
    reloadApp,
  };
}

/**
 * Set up global error handler for chunk load failures.
 * Auto-reloads once per session to recover from stale assets after a deployment.
 */
export function setupChunkErrorHandler() {
  // Shared key with the retry wrapper and error-boundary backstop so the
  // chunk-recovery paths never loop. An in-memory flag backs up sessionStorage
  // in case it's unavailable (private mode / some extension contexts) — without
  // it a persistent chunk failure could reload endlessly.
  const RELOAD_KEY = CHUNK_RELOAD_KEY;
  let reloadedInMemory = false;

  function alreadyReloaded(): boolean {
    if (reloadedInMemory) return true;
    try {
      return !!sessionStorage.getItem(RELOAD_KEY);
    } catch {
      return false;
    }
  }

  function handleChunkError(detail: string) {
    console.error("Chunk load error detected - reloading app");
    console.error("Error details:", detail);

    if (preventReloadFlag) {
      console.warn("Reload prevented - critical data being displayed");
      return;
    }

    // Prevent infinite reload loops: only auto-reload once per session
    if (alreadyReloaded()) {
      console.error("Already reloaded for chunk error - not reloading again");
      return;
    }

    reloadedInMemory = true;
    try {
      sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
    } catch {
      // in-memory flag still prevents a loop
    }
    hardReload();
  }

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.message)) {
      handleChunkError(event.message);
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason?.message)) {
      handleChunkError(event.reason.message);
    }
  });
}
